import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { logAdminAction } from "@/admin/services/adminService";
import { useAdminAuth } from "./useAdminAuth";
import type { AdminRole } from "@/admin/types/admin";

export interface AdminTeamMember {
  adminUserId: string;
  userId: string;
  email: string;
  displayName: string;
  avatarUrl: string | null;
  adminRole: AdminRole;
  createdAt: string;
  createdByDisplayName: string;
}

export function useAdminTeam() {
  const queryClient = useQueryClient();
  const { adminUserId } = useAdminAuth();

  const query = useQuery({
    queryKey: ["admin-team"],
    queryFn: async (): Promise<AdminTeamMember[]> => {
      const { data, error } = await supabase.rpc("admin_list_admins" as any);
      if (error) throw error;
      return (data ?? []).map((row: any) => ({
        adminUserId: row.admin_user_id,
        userId: row.user_id,
        email: row.email ?? "",
        displayName: row.display_name ?? row.email ?? "Inconnu",
        avatarUrl: row.avatar_url ?? null,
        adminRole: row.admin_role as AdminRole,
        createdAt: row.created_at,
        createdByDisplayName: row.created_by_display_name ?? "Système",
      }));
    },
  });

  const promoteMutation = useMutation({
    mutationFn: async ({ email, adminRole }: { email: string; adminRole: AdminRole }) => {
      const { data, error } = await supabase.rpc("admin_promote_to_admin" as any, {
        p_email: email,
        p_admin_role: adminRole,
      });
      if (error) throw error;
      const result = data as { success: boolean; error?: string; admin_user_id?: string };
      if (!result.success) throw new Error(result.error ?? "unknown_error");
      return result;
    },
    onSuccess: async (result, { email, adminRole }) => {
      if (adminUserId && result.admin_user_id) {
        await logAdminAction({
          adminUserId,
          actionType: "user_reactivated", // réutilise un type existant — à étendre si besoin
          targetType: "admin_user",
          targetId: result.admin_user_id,
          notes: `Promotion en ${adminRole} pour ${email}`,
        });
      }
      toast.success("Compte admin créé avec succès");
      queryClient.invalidateQueries({ queryKey: ["admin-team"] });
    },
    onError: (err: Error) => {
      const messages: Record<string, string> = {
        user_not_found: "Aucun compte Hirafi trouvé avec cet email",
        already_admin: "Cet utilisateur est déjà administrateur",
        cannot_remove_self: "Vous ne pouvez pas vous retirer vous-même",
        cannot_remove_owner: "Impossible de retirer un Owner",
      };
      toast.error(messages[err.message] ?? "Erreur lors de la création du compte admin");
    },
  });

  const removeMutation = useMutation({
    mutationFn: async (adminTeamMemberId: string) => {
      const { data, error } = await supabase.rpc("admin_remove_admin" as any, {
        p_admin_user_id: adminTeamMemberId,
      });
      if (error) throw error;
      const result = data as { success: boolean; error?: string };
      if (!result.success) throw new Error(result.error ?? "unknown_error");
      return { adminTeamMemberId };
    },
    onSuccess: async ({ adminTeamMemberId }) => {
      if (adminUserId) {
        await logAdminAction({
          adminUserId,
          actionType: "user_deleted",
          targetType: "admin_user",
          targetId: adminTeamMemberId,
          notes: "Révocation des droits admin",
        });
      }
      toast.success("Droits admin révoqués");
      queryClient.invalidateQueries({ queryKey: ["admin-team"] });
    },
    onError: (err: Error) => {
      const messages: Record<string, string> = {
        not_found: "Administrateur introuvable",
        cannot_remove_self: "Vous ne pouvez pas vous retirer vous-même",
        cannot_remove_owner: "Impossible de retirer un Owner",
      };
      toast.error(messages[err.message] ?? "Erreur lors de la révocation");
    },
  });

  return { query, promoteMutation, removeMutation };
}
